import { Eye } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import type { Disease } from "@/lib/diseases-api";
import { diseaseDisplayName, diseaseInitial } from "./diseaseUtils";

interface DiseaseTableRowProps {
  disease: Disease;
  visibleColumns: Set<"short_name" | "icd10_code" | "status" | "flags">;
  onView: () => void;
}

export const DiseaseTableRow = ({
  disease,
  visibleColumns,
  onView,
}: DiseaseTableRowProps) => (
  <TableRow
    className="cursor-pointer transition-colors hover:bg-muted/30"
    onClick={onView}
  >
    <TableCell className="min-w-[320px] align-middle">
      <div className="flex min-w-0 items-center gap-3">
        <Badge
          variant="neutral"
          className="h-8 min-w-8 justify-center rounded-md font-medium"
        >
          {diseaseInitial(diseaseDisplayName(disease))}
        </Badge>
        <div className="min-w-0 flex-1">
          <div className="z-body truncate text-foreground">
            {diseaseDisplayName(disease)}
          </div>
          {disease.display_name &&
          disease.display_name !== disease.long_name ? (
            <div className="z-caption truncate text-muted-foreground">
              {disease.long_name}
            </div>
          ) : null}
          {disease.other_names ? (
            <div className="z-caption truncate text-muted-foreground">
              {disease.other_names}
            </div>
          ) : null}
        </div>
      </div>
    </TableCell>
    {visibleColumns.has("short_name") ? (
      <TableCell>
        <Badge variant="secondary">{disease.short_name || "-"}</Badge>
      </TableCell>
    ) : null}
    {visibleColumns.has("icd10_code") ? (
      <TableCell>
        {disease.icd10_code ? (
          <Badge variant="neutral" className="font-mono">
            {disease.icd10_code}
          </Badge>
        ) : (
          <span className="z-secondary text-muted-foreground">-</span>
        )}
      </TableCell>
    ) : null}
    {visibleColumns.has("status") ? (
      <TableCell>
        <Badge variant={disease.is_active ? "success" : "neutral"}>
          {disease.is_active ? "Active" : "Inactive"}
        </Badge>
      </TableCell>
    ) : null}
    {visibleColumns.has("flags") ? (
      <TableCell>
        <div className="flex flex-wrap gap-1.5">
          {disease.widely_prevalent ? (
            <Badge variant="secondary">Prevalent</Badge>
          ) : null}
          {disease.family_linked ? (
            <Badge variant="secondary">Family linked</Badge>
          ) : null}
          {disease.is_featured ? (
            <Badge variant="secondary">Featured</Badge>
          ) : null}
          {!disease.widely_prevalent &&
          !disease.family_linked &&
          !disease.is_featured ? (
            <span className="z-secondary text-muted-foreground">-</span>
          ) : null}
        </div>
      </TableCell>
    ) : null}
    <TableCell className="text-right">
      <Button
        variant="outline"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          onView();
        }}
      >
        <Eye className="mr-1.5 h-3.5 w-3.5" />
        View
      </Button>
    </TableCell>
  </TableRow>
);
